import React from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import { isEmpty } from "lodash";
import { Navbar } from "./components/Navbar";
import { Layout } from "./components/Layout";
import { InsuranceSummaryCard } from "./components/InsuranceSummaryCard";
import { DocumentType, InsuranceFormData } from "./types";
import { Introduction } from "./components/Introduction";
import { Fieldset } from "./components/Fieldset";
import { DocumentAcknowledgment } from "./components/DocumentAcknowledgment";
import styles from "./InsuranceReview.module.scss";
import { useProgressGuard } from "./hooks/useProgressGuard";
import * as paths from "features/routing/paths";
import { Button } from "components/Button";
import { createInsuranceCoverageRequest } from "api/createInsuranceCoverageRequest";
import { useCurrentPatient } from "features/patientDashboard/hooks/useCurrentPatient";
import { useCurrentAppointment } from "features/patientDashboard/hooks/useCurrentAppointment";
import { useSnackbar } from "features/patientDashboard/hooks/useSnackbar";
import { selectInsuranceData } from "features/insuranceDataSlice";
import {
  useCheckInPageTracking,
  useCheckInNavigationTracking,
  useCheckInTracking,
} from "features/checkIn/hooks/useCheckInTracking";

export const InsuranceReview: React.FC<{
  insuranceData: InsuranceFormData | null;
}> = () => {
  useCheckInPageTracking({ step: 6, page: "review_your_coverage" });
  const hasSubmittedRef = React.useRef(false);
  useCheckInNavigationTracking(
    { step: 6, page: "review_your_coverage" },
    hasSubmittedRef
  );
  const { trackCheckInEvent } = useCheckInTracking({
    step: 6,
    page: "review_your_coverage",
  });
  const event = (action: string) => `step_6_dci_${action}`;
  const history = useHistory();
  const { handleUnauthorized } = useCurrentPatient();
  const { fetchCurrentAppointment } = useCurrentAppointment();
  const insuranceReviewFormMethods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = insuranceReviewFormMethods;
  const { displayMessage } = useSnackbar();
  //useProgressGuard({ field: "insurance" });
  const insuranceDataArr = useSelector(selectInsuranceData);
  if (isEmpty(insuranceDataArr[0]) && isEmpty(insuranceDataArr[1]))
    return <Redirect to={paths.checkInHome()} />;
  const onSubmit = async () => {
    await handleUnauthorized(async () => {
      try {
        await createInsuranceCoverageRequest(insuranceDataArr);
        await fetchCurrentAppointment();
        history.push(paths.checkInHome());
      } catch (e) {
        if (e.code === "422") {
          trackCheckInEvent("already_submitted");
          displayMessage({ icon: "checkmark", text: "Already submitted." });
          return;
        }
        hasSubmittedRef.current = false;
        trackCheckInEvent("api_failure", {
          api: "create_insurance_coverage",
        });
        throw e;
      }
    });
  };

  return (
    <FormProvider {...insuranceReviewFormMethods}>
      <Layout
        navbar={<Navbar currentStep={2} totalSteps={2} title={"Insurance"} />}
        primaryCta={
          <Button
            onClick={handleSubmit(
              async () => {
                hasSubmittedRef.current = true;
                trackCheckInEvent(event("save_continue"));
                await onSubmit();
              },
              () => {
                trackCheckInEvent("validation_error");
              }
            )}
            renderAsDisabled={!isValid}
            loading={isSubmitting}
            disabled={isSubmitting}
            text="Save & Continue"
          />
        }
        emptyHeight={true}
      >
        <Introduction header="Review your coverage">
          We use this information to verify your coverage, please ensure details
          are accurate.
        </Introduction>
        {insuranceDataArr.map(
          // eslint-disable-next-line array-callback-return
          (insuranceData: InsuranceFormData, index: number) =>
            !isEmpty(insuranceData) && (
              <InsuranceSummaryCard
                key={`insurance-summary-${index}`}
                insurance={insuranceData}
                currentIndex={index}
              />
            )
        )}
        <hr className={styles.separator} />
        <div className={styles.agreement}>
          <Fieldset
            legend="Insurance Assignment Policy & Agreement"
            legendClassName={styles.alwaysVisible}
          >
            <DocumentAcknowledgment
              documentType={DocumentType.InsuranceAssignmentPolicy}
              trackingContext={{ step: 6, page: "review_your_coverage" }}
            />
          </Fieldset>
        </div>
      </Layout>
    </FormProvider>
  );
};
